import React, { useRef, Fragment } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import "./ServicesPage.css";

// icons
import designIcon from "../../assets/services/services1.png";
import rtlIcon    from "../../assets/services/services2.png";
import ipIcon     from "../../assets/services/services3.png";
import socIcon    from "../../assets/services/services4.png";
import lpIcon     from "../../assets/services/services5.png";
import verifyIcon from "../../assets/services/services6.png";
import uvmIcon    from "../../assets/services/services11.png";
import funcIcon   from "../../assets/services/services12.png";
import formalIcon from "../../assets/services/services9.png";
import regIcon    from "../../assets/services/services10.png";

const services = [
  {
    id: "asic-design",
    icon: designIcon,
    title: "ASIC Design",
    description: `Our ASIC Design service covers the full chip creation flow—from architectural
to physical implementation. We use industry‑leading EDA tools to optimize for
power, performance and area. Our team’s RTL coding, synthesis and timing
closure expertise ensures your design meets the highest reliability
standards. Whether you need standard cell or custom methodologies, we’ve
got you covered.`,
    path: "/services/asic-design",
    subs: [
      {
        id: "rtl",
        label: "Front‑End RTL Design",
        description: `We craft synthesizable RTL in SystemVerilog, emphasizing code clarity,
modularity and reusability. Our RTL team ensures smooth handoff to
downstream stages with a focus on performance constraints and low power
techniques. Rigorous linting and static checks are baked into our workflow.`,
        path: "/services/asic-design/front-end-rtl-design",
        icon: rtlIcon,
      },
      {
        id: "ip",
        label: "IP Development",
        description: `From memory controllers to custom accelerators, we develop robust,
parameterizable IP that plugs right into your SoC. Each block is
thoroughly documented and tested across multiple corner cases. We
deliver both behavioral models and synthesizable netlists.`,
        path: "/services/asic-design/ip-development",
        icon: ipIcon,
      },
      {
        id: "soc",
        label: "SoC Integrations",
        description: `We integrate multiple IP blocks, interconnect fabrics and
processors into a cohesive SoC. Our verification team writes
meaningful testplans and harnesses both UVM and directed testbenches
to validate system interactions and power states.`,
        path: "/services/asic-design/soc-integration",
        icon: socIcon,
      },
      {
        id: "lp",
        label: "Low‑Power Design",
        description: `When power is king, our clock‑gating, power‑gating and multi‑voltage
domain expertise shines. We architect regulatory schemes to meet
stringent low‑power budgets without sacrificing performance.`,
        path: "/services/asic-design/low-power-design",
        icon: lpIcon,
      },
    ],
  },
  {
    id: "asic-verification",
    icon: verifyIcon,
    title: "ASIC Verification",
    description: `Our ASIC Verification service guarantees your design functions
exactly as intended under all conditions. We develop UVM‑based
testbenches, create coverage goals and automate regression flows.
Dynamic and formal methodologies are combined to catch corner‑case bugs.
We deliver comprehensive reports and instrumentation to track quality.`,
    path: "/services/asic-verification",
    subs: [
      {
        id: "uvm",
        label: "UVM‑based Testbenches",
        description: `Using SystemVerilog UVM, we build layered environments
with drivers, monitors, sequences and scoreboards. Our reusable
components accelerate coverage closure and foster easy
scalability across projects.`,
        path: "/services/asic-verification/uvm-based-testbenches",
        icon: uvmIcon,
      },
      {
        id: "func",
        label: "Functional Verification",
        description: `Our directed and random stimulus verify every aspect of
your design functionality. We write concise, maintainable tests
that maximize bug hunts while integrating seamlessly with CI flows.`,
        path: "/services/asic-verification/functional-verification",
        icon: funcIcon,
      },
      {
        id: "formal",
        label: "Formal Verification",
        description: `Complementing simulation, our formal teams deploy property
checkers to exhaustively prove critical properties. We identify
corner‑case deadlocks and unreachable states before silicon.`,
        path: "/services/asic-verification/formal-verification",
        icon: formalIcon,
      },
      {
        id: "reg",
        label: "Regression Testing & Coverage",
        description: `We set up nightly regressions across multiple tool flows
and PVT corners, tracking functional and code coverage.
Dashboard reports let you visualize progress and focus on
gaps.`,
        path: "/services/asic-verification/regression-testing-and-coverage",
        icon: regIcon,
      },
    ],
  },
];

export default function ServicesPage() {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-100px 0px -100px 0px" });

  // New: Track global page scroll
  const { scrollYProgress } = useScroll();

  // New: Map scroll to color
  const bannerBackground = useTransform(
    scrollYProgress,
    [0, 0.5],  // start and end scroll points (tweak this if needed)
    ["#0f1f3b", "#ffffff"] // from white to dark blue
  );

  const titleColor = useTransform(
    scrollYProgress,
    [0, 0.5], 
    ["#ffffff", "#0f1f3b"] // from white to black
  );

  return (
    <>
      {/* Full‑width banner */}
      <motion.div
        className="services-banner-wrapper"
        style={{ backgroundColor: bannerBackground }}
      >
        <div className="services-blog-banner">
          <div className="services-banner-overlay">
          <motion.h1 style={{ color: titleColor }}>
  Our Services
</motion.h1>
          </div>
        </div>
      </motion.div>

      {/* Main content */}
      <motion.div
        className="services-page"
        ref={ref}
        initial={{ opacity: 0 }}
        animate={{ opacity: inView ? 1 : 0 }}
        transition={{ duration: 0.6 }}
      >
        {services.map((svc, idx) => (
          <Fragment key={svc.id}>
            <div className="service-section">
              <Link to={svc.path} className="main-card">
                <img src={svc.icon} alt="" className="main-card-icon" />
                <h2 className="main-card-title">{svc.title}</h2>
                <p className="card-desc">{svc.description}</p>
              </Link>
              <div className="sub-grid">
                {svc.subs.map((sub) => (
                  <Link key={sub.id} to={sub.path} className="sub-card">
                    <img src={sub.icon} alt="" className="sub-card-icon" />
                    <div>
                      <span className="sub-card-label">{sub.label}</span>
                      <p className="sub-desc">{sub.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            {idx === 0 && <hr className="service-separator" />}
          </Fragment>
        ))}
      </motion.div>
    </>
  );
}